import React from "react";
import { motion } from "framer-motion";
import PageTransition from "../components/PageTransition";
import Leaf from "../components/Leaf";
import { business, hours } from "../data/content";

const steps = [
  {
    n: "01",
    title: "Choose your treatment",
    detail:
      "Massage, facial, body treatment or waxing — pick what your body needs.",
  },
  {
    n: "02",
    title: "Pick a therapist & time",
    detail: "Filter by availability, or choose a therapist you've seen before.",
  },
  {
    n: "03",
    title: "Confirm your booking",
    detail:
      "You'll get an instant confirmation, plus a reminder before your visit.",
  },
];

export default function Booking() {
  return (
    <PageTransition>
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-gradient text-cream">
        <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-cream/10 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-ink/15 blur-3xl" />

        <div className="container-page relative py-32 md:py-44">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto max-w-xl text-center"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-cream/25 px-4 py-1.5 text-xs tracking-wide text-cream/80">
              <Leaf className="h-3.5 w-3.5 text-cream" />
              Online booking
            </div>

            <h1 className="mt-7 text-display-lg text-cream">
              Book your session
            </h1>

            <p className="mt-5 text-cream/80 leading-relaxed">
              Choose your treatment, therapist and time on our booking page — it
              opens in our online scheduler, hosted by Caljn.
            </p>

            <motion.a
              href={business.bookingUrl}
              whileHover={{ y: -2 }}
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-cream px-9 py-4 text-lg font-medium text-darkgreen shadow-lg shadow-black/15 transition-shadow hover:shadow-xl"
            >
              Continue to booking
              <span aria-hidden="true">→</span>
            </motion.a>

            <p className="mt-6 text-sm text-cream/65">
              Prefer to talk first? Call{" "}
              <a
                href={business.phoneHref}
                className="text-cream underline underline-offset-4 decoration-cream/40 hover:decoration-cream"
              >
                {business.phoneDisplay}
              </a>
              .
            </p>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-cream">
        <div className="container-page py-20 md:py-24">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.15 } },
            }}
            className="grid gap-10 md:grid-cols-3 md:gap-8"
          >
            {steps.map((step) => (
              <motion.div
                key={step.n}
                variants={{
                  hidden: { opacity: 0, y: 24 },
                  show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
                  },
                }}
              >
                <span className="font-display text-4xl text-lightgreen">
                  {step.n}
                </span>
                <p className="mt-4 font-display text-xl text-darkgreen">
                  {step.title}
                </p>
                <p className="mt-2 text-sm text-ink/65 leading-relaxed">
                  {step.detail}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Hours & policies */}
      <section className="border-t border-ink/10 bg-stone/40">
        <div className="container-page grid gap-6 py-16 md:grid-cols-2 md:gap-8">
          <div className="rounded-3xl bg-cream p-8 shadow-sm shadow-ink/5 md:p-10">
            <h2 className="font-display text-2xl text-darkgreen">
              Open every day
            </h2>
            <ul className="mt-5 divide-y divide-ink/10">
              {hours.map((h) => (
                <li
                  key={h.day}
                  className="flex justify-between py-2.5 text-ink/70"
                >
                  <span>{h.day}</span>
                  <span>{h.time}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl bg-cream p-8 shadow-sm shadow-ink/5 md:p-10">
            <h2 className="font-display text-2xl text-darkgreen">
              Good to know
            </h2>
            <ul className="mt-5 space-y-4 text-ink/70">
              <li className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lightgreen" />
                Cancel or reschedule at least 6 hours ahead, free of charge.
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lightgreen" />
                Late cancellations (under 6 hours) are charged at 50%.
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lightgreen" />
                No-shows are charged the full service fee.
              </li>
            </ul>
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
